/**
 * AR Aging Chart Component
 *
 * Horizontal bar chart displaying accounts receivable aging buckets.
 * Color-coded by urgency: green (0-30), yellow (31-60), orange (61-90), red (90+).
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { AgingBucket } from '../../../types/financialInsights';
import {
  formatChartCurrency,
  getAgingBucketColor,
} from '../../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ARAgingChartProps {
  data: AgingBucket[] | undefined;
  loading: boolean;
}

/**
 * Loading skeleton for chart
 */
const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: 350 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="rectangular" width={200} height={32} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={290} />
  </Box>
);

/**
 * AR Aging Chart
 */
export const ARAgingChart: React.FC<ARAgingChartProps> = ({
  data,
  loading,
}) => {
  const theme = useTheme();

  // Show skeleton during loading
  if (loading) {
    return (
      <Card>
        <CardContent>
          <ChartSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{ height: 350 }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No AR Aging Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No outstanding receivables found
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = {
    labels: data.map(bucket => bucket.label),
    datasets: [
      {
        label: 'Amount',
        data: data.map(bucket => bucket.amount),
        backgroundColor: data.map(bucket => getAgingBucketColor(bucket.label)),
        borderColor: data.map(bucket => getAgingBucketColor(bucket.label)),
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        callbacks: {
          label: (context: any) => {
            const bucket = data[context.dataIndex];
            return [
              `Amount: ${formatChartCurrency(bucket.amount)}`,
              `Count: ${bucket.count} invoice${bucket.count !== 1 ? 's' : ''}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
          callback: (value: any) => {
            const numValue = Number(value);
            if (Number.isNaN(numValue)) return '';
            return formatChartCurrency(numValue, 'USD', true);
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
        },
      },
    },
  };

  // Calculate totals
  const totalAmount = data.reduce((sum, bucket) => sum + bucket.amount, 0);
  const totalCount = data.reduce((sum, bucket) => sum + bucket.count, 0);

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Accounts Receivable Aging
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 290, position: 'relative' }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>

        {/* Summary statistics */}
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            Total Outstanding: {formatChartCurrency(totalAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalCount} invoice{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ARAgingChart;
